import React from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'

import {PriceTrendDown} from '../../icons/PriceTrendDown'
import {PriceTrendSimilarHotelsDown} from '../../icons/PriceTrendSimilarHotelsDown'
import {PriceTrendSimilarHotelsUp} from '../../icons/PriceTrendSimilarHotelsUp'
import {PriceTrendStable} from '../../icons/PriceTrendStable'
import {PriceTrendUp} from '../../icons/PriceTrendUp'
import {PriceTrendIconKey, PriceTrendInfoContent} from '../../types'
import {priceTrendMessages} from '../../utils/messages'

const StyledIcon = styled(Icon)`
  display: inline-block;
  > svg {
    vertical-align: middle;
  }
`

const LabelWithIconWrapper = styled(Text)`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s100};
`

const TooltipWrapper = styled.div`
  min-width: 0;
  flex: 1;
`

const IconWrapper = styled('div')`
  border-radius: 50%;
  width: 36px;
  height: 36px;
  background-color: ${cssTheme.colors.background.brand.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ICONS_BY_KEY: Record<PriceTrendIconKey, React.ComponentType> = {
  hotelUp: PriceTrendUp,
  hotelDown: PriceTrendDown,
  hotelStable: PriceTrendStable,
  similarUp: PriceTrendSimilarHotelsUp,
  similarDown: PriceTrendSimilarHotelsDown
}

interface PriceTrendInfoCardProps {
  content: PriceTrendInfoContent
  // Used for test-ids
  infoCardName?: string
  hideIcon?: boolean
  onTooltipClick?: (infoCardName: string) => void
}

export const PriceTrendInfoCard = ({
  content,
  infoCardName,
  hideIcon = false,
  onTooltipClick
}: PriceTrendInfoCardProps) => {
  const {isMobile} = useDeviceLayout()
  const IconComponent = ICONS_BY_KEY[content.icon]
  const {formatMessage} = useIntl()

  const titleLabel = formatMessage(
    priceTrendMessages[content.title.messageKey],
    content.title.values
  )
  const subtitleLabel = formatMessage(
    priceTrendMessages[content.subtitle.messageKey],
    content.subtitle.values
  )
  const tooltipLabel = formatMessage(
    priceTrendMessages[content.tooltip.messageKey],
    content.tooltip.values
  )

  const handleTooltipOpen = (infoCardName: string) => {
    onTooltipClick?.(infoCardName)
  }

  return (
    <Box
      data-testid={infoCardName}
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      paddingLeft={isMobile ? 's400' : 's500'}
      paddingRight={isMobile ? 's400' : 's500'}
      paddingTop="s500"
      paddingBottom="s500"
      style={{width: '100%'}}
      borderColor="border.neutral.secondary"
      borderWidth="bw100"
      borderStyle="solid"
      borderRadius="lg"
    >
      <TooltipWrapper>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          style={{width: '100%'}}
          gap="s400"
        >
          <Box display="flex" flexDirection="column" gap="s100">
            <Text variant="titleS" colorPath="content.neutral.primary">
              {titleLabel}
            </Text>

            <LabelWithIconWrapper
              variant="bodyXS"
              colorPath="content.neutral.secondary"
            >
              {subtitleLabel}
              <Tooltip
                onOpen={() => handleTooltipOpen(infoCardName)}
                placement={TooltipPlacement.Top}
                isInline={false}
                content={
                  <Text variant="bodyS" colorPath="content.neutral.inverse">
                    {tooltipLabel}
                  </Text>
                }
              >
                <StyledIcon name="Information" size="sm" />
              </Tooltip>
            </LabelWithIconWrapper>
          </Box>
          {!hideIcon && (
            <IconWrapper>
              <IconComponent />
            </IconWrapper>
          )}
        </Box>
      </TooltipWrapper>
    </Box>
  )
}
